import { Component, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild, WritableSignal, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SignalsStoreService } from '../signals-store.service';
import { AuthService } from '../../authentication/auth.service';
import { LocalStorageService } from '../local-storage.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { ModalContentService } from '../modal-content/modal-content.service';
import { ModalContentTypes } from '../constants/modal-content-types';
import { FormsModule } from '@angular/forms';
import { ModalContentData, SignupOnboardingSteps } from '../modal-content/modal-content';
import { DeliveryInformation } from '../../settings/account/deliveries/intarfaces';
import { isPlatformBrowser, CommonModule, Location } from '@angular/common';
import { environment } from '../../../environments/environment';
import { Observable, catchError, filter, map, of, take, tap } from 'rxjs';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { HeaderService } from './header.service';
import { MenuService } from '../menu.service';
import { ProductsService } from '../../product/products.service';
import { DISCLAIMER_TYPES, FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from '../constants/databases';
import { Session } from '../types/session.type';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OrderService } from '../order.service';
import { SubscriptionsService } from '../../settings/account/subscriptions/subscriptions.service';
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/autocomplete";
import { MatSelect } from "@angular/material/select";
import { ResolutionService } from '../resolution.service';
import { NAVIGATION_ITEMS, NAVIGATION_ITEMS_RIGHT, RIGHT_ELEMENTS, SETTINGS_PAGES } from './header.lists';
import { StockService } from '../../stock/stock.service';
import { AccountService } from '../account.service';
import { eCommercePermissions } from '../types/account.types';
import { formatDateToReadableString } from '../utils/formatting';
import { KlaviyoService } from '../klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from '../klaviyo/events';
import { formatStringForURL } from '../common/utils';
import { Meta, Title } from '@angular/platform-browser';
import { UserPreferences } from '../types/common.types';
import { DateTime } from 'luxon';
import { FirebaseCrudService } from '../firebase-crud.service';
import { RightElement } from './header.types';
import { PastDueService } from '../past-due.service';
import { SidebarService } from '../sidebar/sidebar.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { SidebarCheckList } from '../sidebar/sidebar.types';
import { TagsFilterComponent } from '../shop-header/tags-filter/tags-filter.component';

/** First-login copy for accounts configured with a custom welcome message. */
const CUSTOM_FIRST_LOGIN_MESSAGE = `
      <p>Thank you for joining us on this journey toward sustainability and community support. By shopping with us, you’re not just enjoying fresh, locally-sourced fruits, vegetables, and more—you’re also empowering local farmers and reducing your environmental impact.</p>
      <p>Together, we can nurture our planet and support those who care for it. Let's grow something amazing, one choice at a time. 🌱</p>
      `;

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    FormsModule,
    CommonModule,
    NgbModule,
    TagsFilterComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  signalsStoreService = inject(SignalsStoreService);
  modalContentService = inject(ModalContentService);
  authService = inject(AuthService);
  sidebarService = inject(SidebarService);
  HAS_MEMBERSHIP_FLOW: boolean = !!environment.config.flows.membership;

  private localStorageService = inject(LocalStorageService);
  private deliveriesService = inject(DeliveriesService);
  private router = inject(Router);
  private headerService = inject(HeaderService);
  private menuService = inject(MenuService);
  private productService = inject(ProductsService);
  private activeModal = inject(NgbModal);
  private orderService = inject(OrderService);
  private location = inject(Location);
  private subscriptionsService = inject(SubscriptionsService);
  private resolutionService = inject(ResolutionService);
  private stockService = inject(StockService);
  private productsService = inject(ProductsService);
  private accountService = inject(AccountService);
  private meta = inject(Meta);
  private title = inject(Title);
  private klaviyoService = inject(KlaviyoService);
  private firebaseService = inject(FirebaseCrudService);
  #pastDueService = inject(PastDueService);

  orderProductsQuantity = computed(() => this.getOrderProductsQuantity());
  isSessionLoaded = computed(() => this.signalsStoreService.isSessionLoaded());
  shouldShowCategoriesBar = computed(() => this.signalsStoreService.shouldShowCategoriesBar());
  isCustomBoxSignupFlow = computed(() => this.signalsStoreService.isCustomBoxSignupFlow());
  isInShopComponent = computed(() => this.signalsStoreService.getIsInShopComponent());

  routeSignal = signal(this.router.url);

  isShowingSearchBar = computed(() => !!(this.headerService.displaySearchBar() || this.signalsStoreService.filterByProducer()));
  isSidebarOpen = this.signalsStoreService.isSidebarOpen;

  isShowingSearchMobile = computed(() => (!!(this.headerService.displaySearchBar() || this.signalsStoreService.filterByProducer()) && this.isMobile()));

  userName: WritableSignal<string> = signal('');

  isMobile = computed(() => this.resolutionService.isMobile());

  landingPages = signal(NAVIGATION_ITEMS);
  landingPagesRight = signal(NAVIGATION_ITEMS_RIGHT);
  settingsPages = computed(() => this.setUpSettingsPermissions());
  rightElements = computed(() => this.setUpRightElementPermissions());

  isShowingMobileFooter = this.signalsStoreService.isShowingMobileFooter;

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  isContentLoading = computed(() => this.stockService.isLoading() || this.productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.productsService.isLoaded());

  session = computed(() => this.signalsStoreService.sessionSignal());

  hasGlobalMessage = computed(() => !!this.signalsStoreService.globalMessages().length);

  @ViewChild(MatSelect) matSelect!: MatSelect;

  subcategorySelected!: number;

  isShopComponent = computed(() => this.signalsStoreService.getIsInShopComponent());

  hasSession = computed(() => this.signalsStoreService.hasSession());

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.#categoryInViewPort();
  }

  #categoryInViewPort() {
    toObservable(
      this.signalsStoreService
        .categoryInViewPort
    )
      .subscribe(obs => {
        if (!obs) return;

        const { categoryId, subCategoryId } = obs;

        const index = this.sidebarService
          .sidebar()
          .findIndex(x => x.id === categoryId);

        if (index > -1) {
          const category = this.sidebarService.sidebar().find(c => c.id === categoryId);
          this.signalsStoreService.selectedCategory.set(category);
          this.subcategorySelected = subCategoryId;
        }
      });
  }

  getOrderProductsQuantity() {
    const odooOrder = this.orderService.odooOrder();
    const allProducts = [
      ...(odooOrder?.products?.common ?? []),
      ...(odooOrder?.products?.subscription?.length ? odooOrder?.products?.subscription.map((e: any) => {
        return {
          ...e,
          _isSub: true
        }
      }) : []),
    ];

    if (odooOrder?.isSkipped) return 0;

    return new Set(allProducts.map((product: any) => {
      const variantId = product.variant.id;
      const packageId = product.package?.id || null;
      const id = `${variantId}${packageId ? `_${packageId}` : ''}`;
      if (product._isSub) return `${id}_sub`;
      return id
    })).size;
  }

  logOut() {
    this.authService.logout();
  }

  ngOnInit(): void {
    if (this.signalsStoreService.filterByProducer()) {
      this.headerService.displaySearchBar.set(true);
    }
    this.subscribeToRouteChanges();
    this.setUpUserSession();
    this.getSubscriptions();
    if (this.isMobile()) {
      this.menuService.fetchMenuCategories().subscribe({
        next: (res) => this.sidebarService.setUpSidebarData({ sidebarData: res.data, isFirstTime: true }),
      });

      this.sidebarService.categoryToNavigate$.pipe(
        filter(item => !!item),
        tap(item => this.productsService.goToSection(item))
      ).subscribe();
    }
  }

  private setUpUserSession() {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    const permissions: eCommercePermissions | null = this.localStorageService.get(LOCALSTORAGE_KEYS.PERMISSIONS);
    if (session?.accountInfo?.id) {
      this.#getUserPreferences(session?.accountInfo?.id);
      this.accountService.getMembershipPermissions().subscribe();
      this.headerService.getFrequencies();
      this.userName.set(session.accountInfo.firstName.toUpperCase());

      if (!(!!(permissions?.settings.paymentMethod.allowed) && !(session?.settings?.hasPaymentMethod)))
        this.getOrder();

      this.deliveriesService.getDeliveryZoneInfo().pipe(tap(() => this.validateNewUserOrFirstLogin())).subscribe();
    }
  }

  toggleSearchBar() {
    // this.isShowingSearchBar.update((current: boolean) => !current);
    this.headerService.updateSearchBarDisplay();
  }
  toggleSearchMobile() {
    this.headerService.updateSearchBarDisplay();
    if (this.signalsStoreService.isFiltering()) this.headerService.clearFilters(true);
  }

  toggleCategorySideMenu() {
    this.signalsStoreService.isSidebarOpen.update(
      (currentValue) => !currentValue
    );
  }

  private subscribeToRouteChanges() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.routeSignal.set(this.router.url);
      });
  }

  private getOrder() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.signalsStoreService.hasSession()) return;
    const currentUrl = this.router.url;
    if (((!currentUrl.includes('/order') || currentUrl.includes('/order-and-billing')) && !currentUrl.includes('/cart')) || this.signalsStoreService.isLimitedUser())
      this.orderService.getOrder(this.signalsStoreService.marketStatus);
  }

  /**
   * The welcome message and the checkout disclaimer used to be two modals stacked
   * on top of each other for a brand new account. They are steps of a single modal
   * now (DEV-10703); the disclaimer step is only added when the customer still
   * owes us the acceptance.
   */
  #openSignupOnboardingModal(isCustomWelcome: boolean, includeCheckoutDisclaimer: boolean) {
    const steps: SignupOnboardingSteps[] = [SignupOnboardingSteps.WELCOME];
    if (includeCheckoutDisclaimer) steps.push(SignupOnboardingSteps.NEW_CHECKOUT_FLOW);

    const hasSession = this.signalsStoreService.hasSession();
    const userData: Session | null = hasSession ? this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) : null;

    // No `title`: each step titles itself, so the shell must not print one of its own.
    const data: ModalContentData = {
      hasSession,
      fullScreenOnMobile: true,
      // The session carries a partial delivery info; the welcome step renders whatever is there.
      welcomeMessage: { ...userData?.deliveryInfo } as DeliveryInformation,
      signupOnboarding: {
        steps,
        welcomeTitle: `Welcome to ${environment.application.name} Food`,
        customWelcomeMessage: isCustomWelcome ? CUSTOM_FIRST_LOGIN_MESSAGE : undefined,
      },
    };

    const modalRef = this.modalContentService.openModal(ModalContentTypes.SIGNUP_ONBOARDING, data);

    // Hand the disclaimer back once the modal is gone: a customer who closed it
    // without accepting still gets the standalone modal on a later order change,
    // exactly as before.
    const release = () => this.signalsStoreService.setIsSignupOnboardingPending(false);
    modalRef.closed.subscribe(release);
    modalRef.dismissed.subscribe(release);
  }

  /**
   * Whether the checkout disclaimer is still pending, answered from the signal when
   * it is already known so a brand new account does not wait on a round trip.
   */
  #isCheckoutDisclaimerPending(): Observable<boolean> {
    const accepted = this.signalsStoreService.isNewCheckoutFlowAccepted();
    if (accepted !== null) return of(!accepted);

    return this.accountService.getDisclaimerStatus(DISCLAIMER_TYPES.NEW_CHECKOUT_FLOW).pipe(
      take(1),
      tap((isAccepted: boolean) => this.signalsStoreService.setIsNewCheckoutFlowAccepted(isAccepted)),
      map((isAccepted: boolean) => !isAccepted),
      // If we cannot tell, the welcome goes out alone and the standalone disclaimer
      // still catches the customer on the next order change.
      catchError(() => of(false)),
    );
  }

  triggerCategoryInfoFetch(
    category: any,
    navigate: boolean = true
  ) {
    this.signalsStoreService.selectedCategory.set(category);
    // if (this.isContentLoading()) return;
    // const formattedCategoryName = formatStringForURL(category.name);
    // const parts = this.location.path().split('/');
    // const currentCategoryName = parts[parts.length - 1].toLowerCase();
    // if (formattedCategoryName === currentCategoryName && navigate && parts.length === 3) return;
    // navigate ? this.router.navigate([`/shop/${formattedCategoryName}`]) : null;
    // this.signalsStoreService.selectedCategory.set(category);
    // this.setUpSEOTags(category.name, formattedCategoryName);
    this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_CATEGORY, category);

    if (category)
      this.goToSection(category);
  }

  /**
   * Past-due warning (DEV-10295). Shown once per browser session so a customer
   * who dismisses it is not nagged on every navigation, and never for suspended
   * accounts, which have their own screen.
   *
   * Returns true only when it actually opens the modal in this load, so the
   * caller knows whether the slot was taken.
   */
  private validatePastDueModal(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const session = this.signalsStoreService.sessionSignal();
    const billing = session?.billing;

    if (!billing?.isAtRisk) return false;
    if (session?.settings?.isSuspended) return false;

    const PAST_DUE_SHOWN_KEY = 'pastDueModalShown';
    if (sessionStorage.getItem(PAST_DUE_SHOWN_KEY)) return false;

    this.modalContentService.openModal(
      ModalContentTypes.ACCOUNT_PAST_DUE,
      {
        closeable: true,
        pastDue: {
          pastDueBalance: billing.pastDueBalance ?? 0,
          daysUntilSuspension: billing.daysUntilSuspension ?? null,
          invoiceHistoryPath: billing.invoiceHistoryPath ?? '/settings/billing/invoice-history',
        },
      },
    );

    sessionStorage.setItem(PAST_DUE_SHOWN_KEY, '1');

    return true;
  }

  private validateNewUserOrFirstLogin() {
    // A past-due warning outranks the welcome message: the customer needs to
    // know their deliveries are at risk before anything else competes for the
    // screen.
    //
    // The flag it reads is a login-time snapshot, so it is checked against the
    // live past-due invoices before anything opens (DEV-10988). Customers who
    // are not flagged get a synchronous answer here, which is why the welcome
    // message is not waiting on a round trip.
    this.#pastDueService.revalidate().subscribe(isAtRisk => {
      if (isAtRisk && this.validatePastDueModal()) return;
      this.validateLoadModals();
    });
  }

  /** The onboarding modal queues behind the past-due warning. */
  private validateLoadModals() {
    const newUser = this.localStorageService.get(LOCALSTORAGE_KEYS.NEW_ACCOUNT);
    const firstLogin = this.localStorageService.get(LOCALSTORAGE_KEYS.FIRST_LOGIN);
    const customFirstLogin = this.session()?.settings.customFirstLoginMessage || false;
    const sessionFirstLogin = this.session()?.settings.firstLogin || false;

    const isCustomWelcome = sessionFirstLogin && customFirstLogin;
    if (!isCustomWelcome && !(newUser || firstLogin)) return;

    // Claim the checkout disclaimer before anything asynchronous runs: it is
    // checked on every order change, and without this the standalone modal would
    // slip in while the steps are still being resolved (DEV-10703).
    this.signalsStoreService.setIsSignupOnboardingPending(true);

    // Both welcome variants consume the flags, so the flags stay a short-lived
    // signal that the onboarding modal owns the disclaimer.
    this.localStorageService.remove(LOCALSTORAGE_KEYS.NEW_ACCOUNT);
    this.localStorageService.remove(LOCALSTORAGE_KEYS.FIRST_LOGIN);

    this.#isCheckoutDisclaimerPending()
      .subscribe((isPending: boolean) => this.#openSignupOnboardingModal(isCustomWelcome, isPending));
  }

  private getSubscriptions() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.signalsStoreService.hasSession()) return;
    this.subscriptionsService.get();
  }

  private setUpSettingsPermissions() {
    const permissions = this.signalsStoreService.permissions();
    if (!permissions) return;

    // Set Up Settings Pages:
    const items = JSON.parse(JSON.stringify(SETTINGS_PAGES))
    const settingPages = items.map((p: { children: any[]; }) => {
      const children = p.children.filter((c: { permissionName: string; }) => {
        const keys = c.permissionName.split('.');
        let permission: any = permissions;
        for (const key of keys) {
          permission = permission[key]
        }
        return permission.allowed
      });
      p.children = children || [];
      return p
    }).filter((p: { children: string | any[]; }) => p.children?.length);

    return settingPages;
  }

  private setUpRightElementPermissions() {
    // Get the local storage permissions variable:
    const permissions: any = this.signalsStoreService.permissions();
    if (!permissions) return;

    // Set Up Right Elements:
    const items = JSON.parse(JSON.stringify(RIGHT_ELEMENTS));
    const rightElements = items.filter((p: { showWithoutPermission: any; permissionName: string; }) => {
      if (p.showWithoutPermission) return true;
      const keys = p.permissionName.split('.');
      let permission = permissions;
      for (const key of keys) {
        permission = permission[key]
      }
      return permission.allowed
    });

    return rightElements || []
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const targetElement = event.target as HTMLElement;
    if (this.matSelect?.panelOpen && !this.matSelect?._elementRef.nativeElement.contains(targetElement)) {
      this.matSelect.close();
    }
  }

  private setUpSEOTags(categoryName: string, formattedCategoryName: string) {
    const { baseTitle, baseURL, type } = environment.config.seo;
    const url = `shop/${formattedCategoryName}`;
    const title = `${categoryName} | ${baseTitle}`;

    // Page title
    this.title.setTitle(title);

    // Open Graph for social networks:
    this.meta.addTag({ property: 'og:title', content: title });
    this.meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.meta.addTag({ property: 'og:type', content: type });
  }

  navigateTo(item: RightElement) {
    const route = item.route || '/shop';

    if (this.signalsStoreService.isInOrderComponent())
      this.localStorageService.set(LOCALSTORAGE_KEYS.SHOULD_SHOW_CART_PREVIEW, !!item.shouldShowCartPreview);

    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (session && !session.settings.hasPaymentMethod) {

      const isAbandoned = session.settings.isAbandoned ?? false;

      return this.modalContentService
        .openModal(
          isAbandoned
            ? ModalContentTypes.PENDING_PAYMENT_METHOD
            : ModalContentTypes.PENDING_PAYMENT_METHOD_NO_SIGNUP,
          {
            title: isAbandoned
              ? 'WELCOME BACK!'
              : 'Payment Method Required',
            closeable: true
          }
        );
    }

    if (item.shouldShowCartPreview && !this.signalsStoreService.isInOrderComponent())
      return this.signalsStoreService.showCartPreview.update(value => !value);

    this.router.navigate([route]);
  }

  #getUserPreferences(userId: number) {
    this.firebaseService
      .getById(FIREBASE_COLLECTIONS.PREFERENCES, userId.toString(), ((result: UserPreferences) => {
        if (!result) {
          return this.signalsStoreService.userPreferences.set({
            dontShowAgainPrevDislike: {
              date: 0,
              value: false
            },
            holdUpSubscriptions: {
              date: 0,
              value: false
            }
          });
        }

        this.signalsStoreService.userPreferences.set(result);
        this.#validateExpiredUserPreferences(userId, result);
      }));
  }

  #validateExpiredUserPreferences(userId: number, preferences: { [key: string]: { value: boolean, date: number } }) {

    let update = false;

    for (const key in preferences) {

      const { value, date } = preferences[key];

      if (!value)
        continue;

      const diffMonths = DateTime.utc().diff(DateTime.fromMillis(date), 'months').months;

      if (diffMonths >= 3) {
        preferences[key].value = false;
        preferences[key].date = 0;
        update = true;
      }
    }

    if (!update)
      return;

    this.firebaseService.update(FIREBASE_COLLECTIONS.PREFERENCES, userId, preferences);
  }

  goToSection(item: SidebarCheckList) {
    this.productsService.goToSection(item);
  }
}
